import type { Orientation } from '../../types'
import type { VisualizerVariant } from '../components/visualizerGeometry'

/**
 * A layout blueprint (#77): the v2 items that reproduce one v1 layout, per
 * orientation, as fractions of the canvas. Transcribed by hand from each
 * layout's JSX — the px literals of its 1080×1920, 1920×1080 and 1080×1080
 * branches — so at those three sizes the items land where v1 drew them.
 * `__tests__/templatePins.test.ts` holds each layout to those numbers (#546).
 *
 * Roles, not colours: `templateItems` resolves a role through the layout's
 * `defaultStyle` and the theme, exactly as the v1 renderer did.
 *
 * What differs from v1 on purpose, for every layout (#546). A blueprint
 * records its own differences in its header comment.
 *
 * - **No flex flow.** v1 laid its parts out with flex, so a short title
 *   moved the bars and the footer up. v2 boxes are fixed at the typical line
 *   count (two title lines on Postcard, three on BigTitle, one on most), and
 *   a text is clamped to the lines its box holds (`maxLines`), as v1's
 *   `line-clamp-N` cut it — what no longer fits is cut, never spilled over
 *   what sits below.
 * - **The speech-bubble tail (Chatbox) is not drawn.** v2 has no triangle
 *   primitive; the box keeps its stroke.
 * - **A video's fit is the scene item's, not the layout's** (#547). Auto (no
 *   stored `fit`) letterboxes a video whose aspect is not its box's over a
 *   blurred copy of itself, as v1 did, and fills the box otherwise; a pick
 *   is Fill (`cover`) or Fit (`contain`), and the backdrop Blur or a colour
 *   (`timeline/fit.tsx`). #552's video slots reuse the per-item fields.
 * - **Paint order is the blueprint's `z`.** v1 ScriptLarge painted its bars
 *   over the footer text; v2 keeps text on top. The visibility recolouring
 *   (`templates.ts`) is v2-only and stays.
 * - **A theme font the catalogue does not know becomes Red Hat Display**:
 *   the schema requires a known font. A layout-pinned family the catalogue
 *   knows (meme's Anton) is kept.
 * - **An audio layout over a video recording drops its background and
 *   visualizer** (the video shows through).
 * - **The bars are a named look, not v1's per-layout pixels.** A visualizer
 *   stores `variant` only (`bold`, `thin`, `equalizer`, `blocks`,
 *   `mirrored`, …) and `visualizerGeometry` owns the numbers, so a creator
 *   picks a look rather than gap and bar widths. v1's dozen bar styles merge
 *   into six; most layouts move by a pixel or two, Postcard's hairline
 *   mirrored bars become Framed's, and Avatar's square ring sits closer to
 *   the portrait (it scales with its box).
 * - **The speaker avatar is a square `cover` picture on SelfPortrait**; v1
 *   kept the image's natural aspect.
 * - **The Zealous mark is not an item.** v1 drew it per layout in the
 *   `logo` colour role when `showZealousLogo` was set; v2 draws one
 *   render-level watermark, white at 50 % with a soft shadow, bottom-right,
 *   for an org on the free plan (`Watermark.tsx`). The `logo` role still
 *   colours the Framed divider, as it did in v1.
 */

/** A `Style` colour role, `white`, `transparent`, or a literal hex colour. */
export type StyleRole =
  | 'background'
  | 'border'
  | 'title'
  | 'subtitle'
  | 'waveform'
  | 'logo'
  | 'speakerName'
  | 'speakerUsername'
  | 'white'
  | 'transparent'
  | `#${string}`

/** Fractions of the canvas width (`x`, `w`) and height (`y`, `h`). */
export type BlueprintBox = { x: number; y: number; w: number; h: number }

type Base = {
  id: string
  box: BlueprintBox
  /** Paint order: higher is on top. */
  z: number
  /**
   * The surface the item sits on, for the visibility rule: an item whose
   * colour equals its surface's is recoloured. `background` (the page) unless
   * the layout draws it on a card in the `border` colour.
   */
  surface?: 'background' | 'border'
}

export type BlueprintSolid = Base & {
  type: 'solid'
  color: StyleRole
  opacity?: number
  /** Fraction of the canvas width. */
  borderRadius?: number
  /** A border inside the box; `width` is a fraction of the canvas width. */
  stroke?: { color: StyleRole; width: number }
}

export type BlueprintText = Base & {
  type: 'text'
  /**
   * Which episode field fills it. `speakerName` / `speakerHandle` make a
   * DYNAMIC text: whoever is speaking on the media track. `literal` draws
   * `literal` as it is (a glyph).
   */
  text: 'title' | 'subtitle' | 'speakerName' | 'speakerHandle' | 'literal'
  literal?: string
  color: StyleRole
  /** Fraction of the canvas width. */
  fontSize: number
  fontWeight: number
  fontStyle?: 'italic'
  lineHeight: number
  align?: 'left' | 'center' | 'right'
  opacity?: number
}

export type BlueprintCaptions = Base & {
  type: 'captions'
  captions: {
    /** The layout's own typography for the orientation. */
    typography: 'layout'
    /** Where the caption block sits inside `box` when shorter than it. */
    align?: 'start' | 'center' | 'end'
    textTransform?: 'uppercase' | 'lowercase' | 'capitalize'
    /**
     * Where v2 departs from the layout's v1 caption typography (Edgy's line
     * height of 1 clipped descenders). The v1 defaults stay untouched: the
     * frozen v1 render path still reads them.
     */
    lineHeight?: number
    linesPerPage?: number
  }
}

export type BlueprintVisualizer = Base & {
  type: 'component'
  componentId: 'audio-visualizer'
  /** The named look; the component owns its numbers (`visualizerGeometry`). */
  props: { variant: VisualizerVariant }
  color: StyleRole
}

export type BlueprintPicture = Base & {
  type: 'component'
  componentId: 'picture'
  props: {
    /**
     * `host`: the episode's host picture (`clipScene.imageUrl`); absent, the
     * item is skipped. `speaker`: whoever is speaking on the media track,
     * resolved at render time — never skipped.
     */
    source: 'host' | 'speaker'
    fit: 'cover' | 'contain'
    shape: 'rect' | 'rounded' | 'circle'
    /** Corner radius in the orientation's reference pixels, over `shape`. */
    radius?: number
    borderColor?: StyleRole
    /** Fraction of the canvas width. */
    borderWidth?: number
  }
  opacity?: number
}

export type BlueprintItem =
  | BlueprintSolid
  | BlueprintText
  | BlueprintCaptions
  | BlueprintVisualizer
  | BlueprintPicture

export type TemplateBlueprint = {
  kind: 'audio' | 'video'
  orientations: Record<Orientation, BlueprintItem[]>
}

const SIZES: Record<Orientation, { w: number; h: number }> = {
  portrait: { w: 1080, h: 1920 },
  landscape: { w: 1920, h: 1080 },
  square: { w: 1080, h: 1080 },
}

/** A box in the orientation's reference pixels, as canvas fractions. */
export function px(
  orientation: Orientation,
  x: number,
  y: number,
  w: number,
  h: number,
): BlueprintBox {
  const size = SIZES[orientation]
  return { x: x / size.w, y: y / size.h, w: w / size.w, h: h / size.h }
}

/** A length in the orientation's reference pixels, as a fraction of its width. */
export function fw(orientation: Orientation, value: number): number {
  return value / SIZES[orientation].w
}

/** The full canvas. */
export const FULL: BlueprintBox = { x: 0, y: 0, w: 1, h: 1 }
